<template>
  <div
    v-on-clickaway="away"
    class="clearfix"
  >
    <input
      ref="searchInput"
      v-model="debouncedSearchText"
      type="search"
      class="form-control p-r-30"
      placeholder="Search Breakdown Levels"
      @keydown.esc="$emit('blur', index);"
    >
    <div 
      class="group-by-dropdown" 
    >
      <!-- Columns list - START -->
      <div
        v-for="(breakdowns, category, categoryIdx) in nonTagsGroupBys"
        :key="categoryIdx"
      >
        <strong class="group-by-dropdown__header text-muted">
          {{ category }}
        </strong>

        <div
          v-for="(group, idx) in breakdowns"
          :key="idx"
          class="group-by-category"
          @click="groupByCategory(group)"
        >
          <span class="text-capitalize group-by-category__main">
            {{ group.displayName }}
          </span>

          <span class="group-by-category__helper">
            {{ group.helperText }}
          </span>
        </div>
      </div>

      <div 
        v-if="availableGroupBys.columns.length && noColumnFound"
        class="text-muted p-l-5"
      >
        No column found
      </div>
      <!-- Columns list - END -->



      <!-- Tag Categories list - START -->
      <strong 
        class="group-by-dropdown__header text-muted m-t-10"
      >
        Tags
      </strong>

      <div 
        v-if="noTagsFound && !noTags"
        class="text-muted p-l-5 f-s-13"
      >
        No tag found
      </div>

      <div
        v-for="(tags, tagCategory) in tagGroupBys"
        :key="tagCategory"
      >
        <div
          class="group-by-category"
          @click="toggleTagsForCategory(tagCategory)"
        >
          <span class="text-capitalize group-by-category__main">
            {{ tagCategory }}
          </span>
  
          <i
            class="fa"
            :class="{'fa-angle-down': !showTagsDropdown[tagCategory], 'fa-angle-up': showTagsDropdown[tagCategory]}"
          />
        </div>

        <div v-if="showTagsDropdown[tagCategory]">
          <div
            v-for="(tag, columnIndex) in tagGroupBys[tagCategory]"
            :key="columnIndex"
            class="p-l-15 clickable group-by-tag__name"
            @click="groupByTag(tag.name, tagCategory)"
          >
            <span class="text-capitalize">
              {{ tag.name }}
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="noTags"
        class="p-l-5 f-s-13"
        :class="{'text-danger': tagGroupingAlreadySelected, 'text-muted': !tagGroupingAlreadySelected}"
      >
        {{ emptyTagsMessage }}
      </div>
      <!-- Tag Categories list - END -->
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mixin as clickaway } from 'vue-clickaway';
import { debounce, isEmpty } from 'lodash';

import messages from './locales';
import sharedMessages from '@/core/locales';

export default {
  name: 'InsightsGroupByDropdown',
  mixins: [ clickaway ],
  props: {
    availableGroupBys: {
      type: Object,
      default() {
        return {
          columns: {
            Level: [{
              field: 'campaignName',
              displayName: 'Campaign Insights',
              helperText: 'Data segmented by campaign'
            }]
          },
          tags: {
            address: ['zip code', 'city', 'state', 'country', 'pincde', 'street 1', 'street 2', 'landmark', 'nearby']
          }
        };
      }
    },
    index: {
      type: Number,
      default: -1
    },
    focusDropdown: {
      type: Boolean,
      default: false
    },
    triggerElement: {
      type: undefined,
      default() {
        return undefined;
      }
    },
    tagGroupingAlreadySelected: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showTagsDropdown: {},
      searchText: ''
    };
  },
  computed: {
    allColumns() {
      let allColumns = [];

      for (const key of Object.keys(this.availableGroupBys.columns)) {
        allColumns = [...allColumns, ...this.availableGroupBys.columns[key]];
      }

      return allColumns;
    },
    allTags() {
      let allTags = [];

      for (const key of Object.keys(this.availableGroupBys.tags)) {
        allTags = [...allTags, ...this.availableGroupBys.tags[key]];
      }

      return allTags;
    },
    nonTagsGroupBys() {
      if (this.searchText) {
        let normalizedSearch = this.searchText.trim().toLowerCase();

        return this.allColumns.reduce((finalValue, col) => {
          if (col?.displayName.toLowerCase().includes(normalizedSearch)) {
            finalValue[col.groupName] = finalValue[col.groupName] || [];
            finalValue[col.groupName].push(col);
          }

          return finalValue;
        }, {});
      }

      return this.availableGroupBys.columns;
    },
    tagGroupBys() {
      if (this.searchText) {
        let normalizedSearch = this.searchText.trim().toLowerCase();

        return this.allTags.reduce((finalValue, tag) => {
          if (tag.tagType.toLowerCase().includes(normalizedSearch) || tag.name.toLowerCase().includes(normalizedSearch)) {
            finalValue[tag.tagType] = finalValue[tag.tagType] || [];
            finalValue[tag.tagType].push(tag);
          }

          return finalValue;
        }, {});
      }

      return this.availableGroupBys.tags;
    },
    debouncedSearchText: {
      get() {
        return this.searchText;
      },
      set: debounce(function setNewGroupBySearchValue(value) {
        // Reset the tag types shown mapping
        if (!this.searchText || !value) {
          this.showTagsDropdown = {};
        }

        this.searchText = value;
      }, 800)
    },
    noTagsFound() {
      return this.searchText && isEmpty(this.tagGroupBys);
    },
    noColumnFound() {
      return this.searchText && isEmpty(this.nonTagsGroupBys);
    },
    triggerId() {
      return 'level' + this.index;
    },
    noTags() {
      return isEmpty(this.tagGroupBys);
    },
    emptyTagsMessage() {
      return this.tagGroupingAlreadySelected ? 'Max 1 tag breakdown per query' : 'No tags';
    }
  },
  watch: {
    tagGroupBys(newValue) {
      if (this.searchText) {
        for (const tagType in newValue) {
          Vue.set(this.showTagsDropdown, tagType, true);
        }
      }
    },
    focusDropdown(newValue) {
      if (newValue) {
        this.$refs['searchInput'].focus();	
      }
    }
  },
  methods: {
    toggleTagsForCategory(tagCategory) {
      Vue.set(this.showTagsDropdown, tagCategory, !this.showTagsDropdown[tagCategory]);
    },
    hideTagsForCategory() {
      this.showTagsDropdown = {};
    },
    groupByTag(tag, selectedTagCategory) {
      this.$emit('input', { field: `tag:${selectedTagCategory}:${tag}`, displayName: `${selectedTagCategory}:${tag}`, originalTag: {
        tagType: selectedTagCategory,
        tagName: tag
      } }, this.index);
    },
    groupByCategory(category) {
      this.$emit('input', { field: category.field, displayName: category.displayName }, this.index);
    },
    away(event) {
      if (this.triggerElement) {
        let path = event.path || (event.composedPath ? event.composedPath() : undefined);

        if (path
          ? path.indexOf(this.triggerElement) < 0
          : false) {
          this.$emit('blur', this.index);
        }

        return;
      }

      if(event.target.id.indexOf(this.triggerId) !== 0 && event.target?.parentElement?.id.indexOf(this.triggerId) !== 0) {
        this.$emit('blur', this.index);
      }
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>